import type { Edge, Node } from "@xyflow/react";
import type {
  LivestreamNodeData,
  MeetingRouteNodeData,
  StartAutomationNodeData,
} from "./types";

const NODE_X_STEP = 340;
const NODE_Y = 80;
const ACCESS_AUTOMATION_Y = 240;

function pagePath(route: string): string {
  return route.startsWith("/") ? route : `/${route}`;
}

function pageHref({ baseUrl, route }: { baseUrl: string; route: string }): string {
  return `${baseUrl.replace(/\/$/, "")}${pagePath(route)}`;
}

export function buildMeetingFlowGraph({
  productId,
  baseUrl,
  accessAutomationId,
  shopAutomationId,
  accessIsSaving,
  shopIsSaving,
  onAccessAutomationChange,
  onShopAutomationChange,
}: {
  productId: string;
  baseUrl: string;
  accessAutomationId?: string;
  shopAutomationId?: string;
  accessIsSaving?: boolean;
  shopIsSaving?: boolean;
  onAccessAutomationChange: (automationId: string) => void;
  onShopAutomationChange: (automationId: string) => void;
}): {
  nodes: Node<MeetingRouteNodeData | LivestreamNodeData | StartAutomationNodeData>[];
  edges: Edge[];
} {
  const nodes: Node<MeetingRouteNodeData | LivestreamNodeData | StartAutomationNodeData>[] = [];
  const edges: Edge[] = [];

  const steps: Array<{
    id: string;
    label: string;
    route?: string;
    isEntry?: boolean;
  }> = [
    { id: "entry", label: "Entry", route: `/meetings/${productId}`, isEntry: true },
    { id: "login", label: "Login", route: `/meetings/${productId}/login` },
    { id: "verify", label: "Verify", route: `/meetings/${productId}/verify` },
  ];

  const entryNodeId = "meeting-entry";
  let previousNodeId: string | null = null;
  let x = 0;
  let edgeType: "submit" | "then" = "submit";

  for (const step of steps) {
    const nodeId = `meeting-${step.id}`;
    nodes.push({
      id: nodeId,
      type: "meetingRoute",
      position: { x, y: NODE_Y },
      data: {
        label: step.label,
        path: step.route ? pagePath(step.route) : undefined,
        href: step.route ? pageHref({ baseUrl, route: step.route }) : undefined,
        isEntry: step.isEntry,
      },
    });

    if (previousNodeId) {
      edges.push({
        id: `${previousNodeId}-${nodeId}`,
        source: previousNodeId,
        sourceHandle: "flow",
        target: nodeId,
        type: edgeType,
      });
      edgeType = "then";
    }

    previousNodeId = nodeId;
    x += NODE_X_STEP;
  }

  const accessAutomationNodeId = "meeting-access-start-automation";
  nodes.push({
    id: accessAutomationNodeId,
    type: "startAutomation",
    position: { x: 0, y: ACCESS_AUTOMATION_Y },
    data: {
      title: "Purchase access",
      automationId: accessAutomationId,
      isSaving: accessIsSaving,
      onAutomationChange: onAccessAutomationChange,
    },
  });
  edges.push({
    id: `${entryNodeId}-${accessAutomationNodeId}`,
    source: entryNodeId,
    sourceHandle: "checkout-success",
    target: accessAutomationNodeId,
    type: "onCheckoutSuccess",
  });

  const livestreamNodeId = "meeting-livestream";
  nodes.push({
    id: livestreamNodeId,
    type: "livestream",
    position: { x, y: NODE_Y },
    data: {},
  });
  if (previousNodeId) {
    edges.push({
      id: `${previousNodeId}-${livestreamNodeId}`,
      source: previousNodeId,
      sourceHandle: "flow",
      target: livestreamNodeId,
      type: "then",
    });
  }
  x += NODE_X_STEP;

  const shopAutomationNodeId = "meeting-shop-start-automation";
  nodes.push({
    id: shopAutomationNodeId,
    type: "startAutomation",
    position: { x, y: NODE_Y },
    data: {
      title: "Shop checkout",
      automationId: shopAutomationId,
      isSaving: shopIsSaving,
      onAutomationChange: onShopAutomationChange,
    },
  });
  edges.push({
    id: `${livestreamNodeId}-${shopAutomationNodeId}`,
    source: livestreamNodeId,
    sourceHandle: "checkout-success",
    target: shopAutomationNodeId,
    type: "onCheckoutSuccess",
  });

  return { nodes, edges };
}
